import { Component, OnInit, Input, OnChanges, SimpleChanges , DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ChangeLogItemService, ChangeLogItem, CreateChangeLogItemDto } from '../../services/change-log-item.service';

@Component({
  selector: 'app-change-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Tag,
    Tooltip,
    Dialog,
    InputText,
    InputNumber,
    Select,
    DatePicker,
    ConfirmDialog,
  ],
  templateUrl: './change-log.component.html',
  styleUrl: './change-log.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class ChangeLogComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  @Input() projectId: string = '';

  items: ChangeLogItem[] = [];
  selectedItem: ChangeLogItem | null = null;
  displayItemDialog: boolean = false;
  isEditItemMode: boolean = false;
  dateRequested: Date | null = null;
  plannedDate: Date | null = null;
  actualDate: Date | null = null;
  loading: boolean = false;
  fieldTouched: Record<string, boolean> = {};

  priorityOptions = [
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' }
  ];

  statusOptions = [
    { label: 'Open', value: 'Open' },
    { label: 'In Progress', value: 'In Progress' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Rejected', value: 'Rejected' }
  ];

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private changeLogItemService: ChangeLogItemService
  ) {}

  ngOnInit() {
    if (this.projectId) {
      this.loadItems();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && this.projectId) {
      this.loadItems();
    }
  }

  loadItems() {
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.changeLogItemService.getByProjectId(this.projectId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (items) => {
        this.items = items;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading change log items:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load change log items. Please try again.'
        });
        this.loading = false;
      }
    });
  }

  showAddItemDialog() {
    if (!this.projectId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Project ID is required to add change log items.'
      });
      return;
    }
    const todayStr = this.formatDateToLocalString(new Date());
    this.selectedItem = {
      id: '',
      projectId: this.projectId,
      crNumber: this.generateCrNumber(),
      dateRequested: todayStr,
      changeDescription: '',
      priority: 'High',
      status: 'Open',
      effortEstimateDays: undefined,
      plannedDate: '',
      actualDate: '',
      comments: ''
    };
    this.dateRequested = new Date();
    this.plannedDate = null;
    this.actualDate = null;
    this.fieldTouched = {};
    this.isEditItemMode = false;
    this.displayItemDialog = true;
  }

  showEditItemDialog(item: ChangeLogItem) {
    this.selectedItem = { ...item };
    this.dateRequested = item.dateRequested ? this.parseLocalDateString(item.dateRequested) : null;
    this.plannedDate = item.plannedDate ? this.parseLocalDateString(item.plannedDate) : null;
    this.actualDate = item.actualDate ? this.parseLocalDateString(item.actualDate) : null;
    this.fieldTouched = {};
    this.isEditItemMode = true;
    this.displayItemDialog = true;
  }

  resetItemForm() {
    this.fieldTouched = {};
    if (!this.selectedItem) return;
    if (this.isEditItemMode) {
      const item = this.items.find(i => i.id === this.selectedItem?.id);
      if (item) {
        this.selectedItem = { ...item };
        this.dateRequested = item.dateRequested ? this.parseLocalDateString(item.dateRequested) : null;
        this.plannedDate = item.plannedDate ? this.parseLocalDateString(item.plannedDate) : null;
        this.actualDate = item.actualDate ? this.parseLocalDateString(item.actualDate) : null;
      }
    } else {
      this.selectedItem.crNumber = '';
      this.selectedItem.changeDescription = '';
      this.selectedItem.priority = 'Medium';
      this.selectedItem.status = 'Requested';
      this.selectedItem.effortEstimateDays = undefined;
      this.selectedItem.comments = '';
      this.selectedItem.dateRequested = '';
      this.selectedItem.plannedDate = undefined;
      this.selectedItem.actualDate = undefined;
      this.dateRequested = null;
      this.plannedDate = null;
      this.actualDate = null;
    }
  }

  saveItem() {
    this.fieldTouched['changeDescription'] = true;

    if (!this.selectedItem || !this.selectedItem.changeDescription.trim()) {
      return;
    }

    if (!this.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required.'
      });
      return;
    }

    if (this.dateRequested) {
      this.selectedItem.dateRequested = this.formatDateToLocalString(this.dateRequested);
    }

    if (this.plannedDate) {
      this.selectedItem.plannedDate = this.formatDateToLocalString(this.plannedDate);
    } else {
      this.selectedItem.plannedDate = undefined;
    }

    if (this.actualDate) {
      this.selectedItem.actualDate = this.formatDateToLocalString(this.actualDate);
    } else {
      this.selectedItem.actualDate = undefined;
    }

    if (this.isEditItemMode) {
      // Update existing item
      this.changeLogItemService.updateItem(this.selectedItem.id, this.selectedItem).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updatedItem) => {
          const index = this.items.findIndex(i => i.id === updatedItem.id);
          if (index !== -1) {
            this.items[index] = updatedItem;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Change log item updated successfully.'
          });
          this.displayItemDialog = false;
          this.selectedItem = null;
        },
        error: (error) => {
          console.error('Error updating change log item:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update change log item. Please try again.'
          });
        }
      });
    } else {
      // Create new item
      const createDto: CreateChangeLogItemDto = {
        projectId: this.projectId,
        crNumber: this.selectedItem.crNumber,
        dateRequested: this.selectedItem.dateRequested,
        changeDescription: this.selectedItem.changeDescription,
        priority: this.selectedItem.priority,
        status: this.selectedItem.status,
        effortEstimateDays: this.selectedItem.effortEstimateDays,
        plannedDate: this.selectedItem.plannedDate,
        actualDate: this.selectedItem.actualDate,
        comments: this.selectedItem.comments
      };

      this.changeLogItemService.addItem(createDto).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (newItem) => {
          this.items.push(newItem);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Change log item added successfully.'
          });
          this.displayItemDialog = false;
          this.selectedItem = null;
        },
        error: (error) => {
          console.error('Error adding change log item:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add change log item. Please try again.'
          });
        }
      });
    }
  }

  deleteItem(item: ChangeLogItem) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${item.changeDescription || ''}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.changeLogItemService.deleteItem(item.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            const index = this.items.findIndex(i => i.id === item.id);
            if (index !== -1) {
              this.items.splice(index, 1);
            }
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Change log item deleted successfully.'
            });
          },
          error: (error) => {
            console.error('Error deleting change log item:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete change log item. Please try again.'
            });
          }
        });
      }
    });
  }

  generateCrNumber(): string {
    if (this.items.length === 0) {
      return 'CR00001';
    }
    const maxNum = Math.max(...this.items.map(i => {
      const match = i.crNumber.match(/\d+/);
      return match ? parseInt(match[0]) : 0;
    }));
    return `CR${String(maxNum + 1).padStart(5, '0')}`;
  }

  /** Parse YYYY-MM-DD as local date (avoids timezone off-by-one). */
  private parseLocalDateString(dateStr: string): Date | null {
    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return null;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    const date = new Date(y, m, d);
    if (isNaN(date.getTime())) return null;
    return date;
  }

  /** Format Date to YYYY-MM-DD using local date. */
  private formatDateToLocalString(date: Date): string {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '-';
    const date = this.parseLocalDateString(dateString);
    if (!date) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  getPrioritySeverity(priority: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (priority) {
      case 'High':
        return 'danger';
      case 'Medium':
        return 'warn';
      case 'Low':
        return 'success';
      default:
        return 'secondary';
    }
  }

  getStatusSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'warn';
      case 'Open':
        return 'danger';
      case 'Rejected':
        return 'secondary';
      default:
        return 'secondary';
    }
  }
}

